/** /delivery-logs/:id/:tab? — delivery-log detail shell.
 *
 *  An orphaned delivery (status 9) also gets a "Recover" button in the header:
 *  it queues the same work the recovery cron does — re-dispatch only what the
 *  crashed delivery never attempted — and the outcome lands back on the row.
 *
 *  Tabs mirror the Django admin's fieldsets for Event_Delivery_Log:
 *   - Overview — the reusable DeliveryLogSummary (DB row + notification rows
 *     + redelivery chain);
 *   - Delivery — the S3 archive's delivery decisions + full notifications tree;
 *   - Debug — rendering context, rendered blocks, composition, raw manifest,
 *     and the S3 console link.
 *  The archive sections come from ONE lazy `GET …/archive/` call (a single S3
 *  fetch server-side), fired only when a tab needing it is opened.
 */
import { Alert, Box, Button, CircularProgress, Stack, Tab, Tabs, Typography } from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';

import * as edlApi from '@/api/eventDeliveryLogs';
import type { DeliveryLogArchive, DeliveryLogDetail } from '@/api/eventDeliveryLogs';
import { JsonEditorWidget } from '@/components/JsonEditor/JsonEditorWidget';
import { PageHeader } from '@/components/PageHeader';
import { SectionCard } from '@/components/ReadView';
import { useSnackbar } from '@/components/Snackbar';
import { act, DetailSubtitle, ValueActions } from '@/components/ValueActions/ValueActions';
import { useAuth } from '@/auth/AuthProvider';

import { AiDiagnosePanel } from './diagnose/AiDiagnosePanel';
import { DeliveryStatusChip, STATUS_ORPHANED } from './deliveryLogsColumns';
import {
  ctxOf,
  eventActions,
  logIdActions,
  recipientActions,
  statusActions,
  tenantDeliveryActions,
} from './overview/deliveryLogLinks';
import { DeliveryLogSummary } from './overview/DeliveryLogSummary';

const TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'delivery', label: 'Delivery' },
  { id: 'debug', label: 'Debug' },
  // Gated by console_event_delivery_ai — filtered out below for other users.
  { id: 'diagnose', label: 'Diagnose' },
] as const;

/** Read-only JSON tree for one archive section (jsoneditor 'view' mode).
 *  Sections may be objects OR arrays — the widget takes both. */
function JsonSection({ title, value }: { title: string; value: unknown }) {
  const empty =
    value == null ||
    (Array.isArray(value) && value.length === 0) ||
    (typeof value === 'object' && Object.keys(value as object).length === 0);
  return (
    <SectionCard title={title}>
      {empty ? (
        <Typography variant="body2" color="text.disabled">
          Nothing recorded.
        </Typography>
      ) : (
        <JsonEditorWidget initialValue={value as Record<string, unknown>} readOnly mode="view" />
      )}
    </SectionCard>
  );
}

/** The human-readable delivery decisions log (delivery.json — one line each). */
function DeliveryLogLines({ lines }: { lines: string[] | null | undefined }) {
  return (
    <SectionCard title="Delivery log">
      {!lines || lines.length === 0 ? (
        <Typography variant="body2" color="text.disabled">
          Nothing recorded.
        </Typography>
      ) : (
        <Box
          component="pre"
          sx={{
            m: 0,
            p: 1.5,
            bgcolor: 'action.hover',
            borderRadius: 1,
            overflowX: 'auto',
            fontFamily: 'monospace',
            fontSize: 12,
            lineHeight: 1.6,
            whiteSpace: 'pre-wrap',
          }}
        >
          {lines.join('\n')}
        </Box>
      )}
    </SectionCard>
  );
}

/** Fetches the archive once (shared by both tabs) and gates its children on
 *  availability — an unavailable archive renders inline, admin-style. */
function ArchiveGate({
  logId,
  children,
}: {
  logId: string;
  children: (archive: DeliveryLogArchive) => ReactNode;
}) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['delivery-logs', 'archive', logId],
    queryFn: () => edlApi.archive(logId),
    staleTime: Infinity, // the archive is immutable
  });

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (error || !data) return <Alert severity="error">Couldn’t load the S3 archive.</Alert>;
  if (!data.available) {
    return (
      <Alert severity="warning">
        {data.error ?? 'No S3 archive is available for this delivery.'}
      </Alert>
    );
  }
  return <>{children(data)}</>;
}

/** Header action for an orphaned delivery. Enabled only when the row is still in
 *  the transient ORPHANED status AND its archive can rebuild the event — the
 *  same two conditions the server enforces (`can_recover`). */
function RecoverButton({ d }: { d: DeliveryLogDetail }) {
  const auth = useAuth();
  const snackbar = useSnackbar();
  const queryClient = useQueryClient();

  // Only fetched for an orphan — everything else never shows the button.
  const { data: archive } = useQuery({
    queryKey: ['delivery-logs', 'archive', d.id],
    queryFn: () => edlApi.archive(d.id),
    staleTime: Infinity,
    enabled: d.status === STATUS_ORPHANED,
  });

  const mutation = useMutation({
    mutationFn: () => edlApi.recover(d.id),
    onSuccess: () => {
      snackbar.show(
        'Recovery queued. The outcome appears on this row (status and the recovery link) '
        + 'once the worker picks it up.',
        'success',
      );
      queryClient.invalidateQueries({ queryKey: ['delivery-logs'] });
    },
    onError: (e: unknown) => {
      snackbar.show(e instanceof Error ? e.message : 'Recovery could not be queued.', 'error');
    },
  });

  if (d.status !== STATUS_ORPHANED || !auth.hasPerm('console_event_delivery_manage')) return null;

  const blocked = archive && archive.can_recover === false;
  return (
    <Button
      size="small"
      variant="outlined"
      startIcon={<RestartAltIcon />}
      disabled={mutation.isPending || blocked}
      onClick={() => mutation.mutate()}
      title={
        blocked
          ? 'This orphan has no usable S3 archive — there is nothing to replay.'
          : 'Re-dispatch only the notifications and delivery methods this delivery never '
            + 'attempted, replaying its archived context and matching decisions.'
      }
    >
      Recover
    </Button>
  );
}

export function DeliveryLogDetailPage() {
  const { id, tab } = useParams<{ id: string; tab?: string }>();
  const navigate = useNavigate();
  const auth = useAuth();

  const { data: d } = useQuery({
    queryKey: ['delivery-logs', 'detail', id ?? ''],
    queryFn: () => edlApi.retrieve(id!),
    enabled: Boolean(id),
  });

  const visibleTabs = TABS.filter(
    (t) => t.id !== 'diagnose' || auth.hasPerm('console_event_delivery_ai'),
  );
  const activeTab = visibleTabs.find((t) => t.id === tab)?.id ?? 'overview';

  if (!id) return <Alert severity="warning">Invalid delivery log id.</Alert>;

  const ctx = d ? ctxOf(d) : { tenantId: null };
  const title = d ? <ValueActions actions={eventActions(d.event, d, ctx)}>{d.event}</ValueActions> : 'Delivery log';
  const subtitle = d ? (
    <DetailSubtitle
      parts={[
        d.tour_operator_id != null && (
          <ValueActions actions={tenantDeliveryActions({ id: d.tour_operator_id })}>
            {d.tour_operator_code ?? `#${d.tour_operator_id}`}
          </ValueActions>
        ),
        <ValueActions key="log" actions={logIdActions(d.log_id, ctx)}>{d.log_id}</ValueActions>,
        (d.user_email || d.user_id != null) && (
          <ValueActions actions={recipientActions(d, ctx)}>{d.user_email ?? `user #${d.user_id}`}</ValueActions>
        ),
        <ValueActions key="uuid" actions={[act.copy(d.id, 'Copy uuid')]}>{d.id}</ValueActions>,
      ]}
    />
  ) : (
    id
  );

  return (
    <Box>
      <PageHeader
        helpStory="delivery-logs"
        section={visibleTabs.find((t) => t.id === activeTab)?.label}
        title={title}
        titleText={d?.event ?? 'Delivery log'}
        subtitle={subtitle}
        actions={
          d ? (
            <Stack direction="row" spacing={1} alignItems="center">
              <ValueActions chip actions={statusActions(d.status_display, ctx)}>
                <DeliveryStatusChip status={d.status} label={d.status_display} />
              </ValueActions>
              <RecoverButton d={d} />
            </Stack>
          ) : undefined
        }
      />

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={(_, v) => navigate(`/delivery-logs/${id}/${v}`)}>
          {visibleTabs.map((t) => (
            <Tab key={t.id} value={t.id} label={t.label} />
          ))}
        </Tabs>
      </Box>

      {activeTab === 'overview' && <DeliveryLogSummary logId={id} showHeader={false} />}

      {activeTab === 'delivery' && (
        <ArchiveGate logId={id}>
          {(a) => (
            <>
              <JsonSection title="Delivery decisions" value={a.delivery_decisions} />
              <DeliveryLogLines lines={a.delivery} />
              <JsonSection
                title="Notifications (rendered messages + per-method results)"
                value={a.notifications}
              />
            </>
          )}
        </ArchiveGate>
      )}

      {activeTab === 'diagnose' && <AiDiagnosePanel edlId={id} logId={d?.log_id} />}

      {activeTab === 'debug' && (
        <ArchiveGate logId={id}>
          {(a) => (
            <>
              <JsonSection title="Rendering context" value={a.rendering_context} />
              <JsonSection title="Rendered blocks" value={a.rendered_blocks} />
              <JsonSection title="Composition" value={a.composition} />
              <JsonSection title="Manifest (raw)" value={a.manifest} />
              {a.s3 && (
                <SectionCard title="S3 archive">
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: 13, mb: 1 }} component="div">
                    <ValueActions actions={[act.copy(`s3://${a.s3.bucket}/${a.s3.key}`, 'Copy S3 path')]}>
                      s3://{a.s3.bucket}/{a.s3.key}
                    </ValueActions>
                  </Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    endIcon={<OpenInNewIcon />}
                    href={a.s3.console_url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Open in the AWS console
                  </Button>
                </SectionCard>
              )}
            </>
          )}
        </ArchiveGate>
      )}

      <Box sx={{ mt: 4, color: 'text.secondary', fontSize: 13 }}>
        <RouterLink to="/delivery-logs" style={{ color: '#0a1a3f' }}>
          ← Back to delivery logs
        </RouterLink>
      </Box>
    </Box>
  );
}
